import React, { useState, useCallback, memo } from 'react';
import { Button } from 'devextreme-react/button';
import { Popup } from 'devextreme-react';
import { LoadIndicator } from 'devextreme-react';
import { TextArea } from 'devextreme-react/text-area';
import { useScreenSize } from '../../../../utils/media-query';
import { useApiConfigUsuarios } from '../../../../api/api.ConfigUsuarios';
import { newToast } from '../../../../components';
import { ToolbarItem } from 'devextreme-react/popup';

interface ModalGenerateTokenProps {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  userId: number;
  userName: string;
}

const ModalGenerateTokenComponent = ({ isModalVisible, setIsModalVisible, userId, userName }: ModalGenerateTokenProps) => {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { generateTokenForUser } = useApiConfigUsuarios();

  const handleGenerateToken = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const result = await generateTokenForUser(userId);
      console.log('Token generated:', result);
      if (result && result.Token) {
        setToken(result.Token);
        newToast('SUCCESS', 'Token generado exitosamente', 3);
      } else {
        newToast('ERROR', 'Error al generar el token', 3);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      newToast('ERROR', 'Error al generar el token', 3);
    } finally {
      setLoading(false);
    }
  }, [userId, generateTokenForUser]);

  const handleCopyToken = useCallback(() => {
    if (token) {
      navigator.clipboard.writeText(token).then(() => {
        newToast('SUCCESS', 'Token copiado al portapapeles', 3);
      }).catch(() => {
        newToast('ERROR', 'Error al copiar el token', 3);
      });
    }
  }, [token]);

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
    setToken('');
  }, [setIsModalVisible]);

  return (
    <Popup
      visible={isModalVisible}
      onHiding={handleClose}
      maxHeight={'90%'}
      dragEnabled={false}
      showCloseButton={true}
      showTitle={true}
      title={`Generar Token - ${userName}`}
      width={600}
      fullScreen={isMedium || isSmall || isXSmall}
      height={'auto'}
    >
      <ToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        options={{
          text: 'Cerrar',
          onClick: handleClose,
          stylingMode: 'outlined'
        }}
      />
      
      <div style={{ padding: '20px' }}>
        {loading && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <LoadIndicator visible={true} />
            <p>Generando token...</p>
          </div>
        )}

        {!token && !loading && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <p style={{ marginBottom: '20px' }}>
              ¿Está seguro que desea generar un nuevo token para el usuario <strong>{userName}</strong>?
            </p>
            <p style={{ marginBottom: '20px', color: '#ff6b6b', fontSize: '14px' }}>
              <strong>Advertencia:</strong> Al generar un nuevo token, el token anterior dejará de funcionar.
            </p>
            <Button
              text="Generar Token"
              type="default"
              stylingMode="contained"
              onClick={handleGenerateToken}
              disabled={loading}
            />
          </div>
        )}

        {token && !loading && (
          <div>
            <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              Token generado exitosamente:
            </p>
            <div style={{ marginBottom: '20px' }}>
              <TextArea
                value={token}
                readOnly={true}
                height={120}
                stylingMode="outlined"
              />
            </div>
            <div style={{ textAlign: 'center', gap: '10px', display: 'flex', justifyContent: 'center' }}>
              <Button
                text="Copiar Token"
                icon="copy"
                type="default"
                stylingMode="contained"
                onClick={handleCopyToken}
              />
              <Button
                text="Generar Nuevo"
                icon="refresh"
                type="normal"
                stylingMode="outlined"
                onClick={handleGenerateToken}
                disabled={loading}
              />
            </div>
            <p style={{ marginTop: '15px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Guarde este token en un lugar seguro. No podrá verlo nuevamente.
            </p>
          </div>
        )}
      </div>
    </Popup>
  );
};

export const ModalGenerateToken = memo(ModalGenerateTokenComponent);
export default ModalGenerateToken;
